import { supabase } from '../../supabase';
import { prefetchFirstRunHint } from '../onboarding/firstRunHint';
import { runAfterAuthHandoff } from '../../ui/splash/authHandoffHold';

/**
 * Eligible tenant context: active membership, accepted invite, active tenant (matches server helpers).
 * If the user only has a sent invite (invited_at set, invite not accepted), returns invitePending.
 */
export async function getTenantMembershipForUser(userId) {
  const { data, error } = await supabase
    .from('tenant_memberships')
    .select('tenant_id, role, tenants!inner(is_active)')
    .eq('user_id', userId)
    .eq('is_active', true)
    .not('invite_accepted_at', 'is', null)
    .eq('tenants.is_active', true)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (data?.tenant_id) {
    return { role: data.role ?? null, tenantId: data.tenant_id ?? null, invitePending: false };
  }

  const { data: pendingRow, error: pendingError } = await supabase
    .from('tenant_memberships')
    .select('id')
    .eq('user_id', userId)
    .eq('is_active', true)
    .not('invited_at', 'is', null)
    .is('invite_accepted_at', null)
    .limit(1)
    .maybeSingle();

  if (pendingError) {
    throw pendingError;
  }

  return {
    role: null,
    tenantId: null,
    invitePending: Boolean(pendingRow),
  };
}

export async function getTenantMetadata(tenantId) {
  if (!tenantId) {
    return { isActive: null, industryType: null, customIndustryLabel: null, tenantName: null };
  }

  // Onboarding (2026-09-30): start the tiny first-run read NOW, in parallel,
  // so a brand-new user's welcome can open on the first frame instead of
  // after the whole dashboard load. Fire-and-forget: never awaited, never
  // throws, never changes this loader's result or timing. It queues behind a
  // sign-in handoff like the rest: nothing but the 2FA decision runs before the code.
  runAfterAuthHandoff(() => { void prefetchFirstRunHint(tenantId); });

  const { data, error } = await supabase
    .from('tenants')
    .select('is_active, industry_type, custom_industry_label, name')
    .eq('id', tenantId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data) {
    return { isActive: false, industryType: null, customIndustryLabel: null, tenantName: null };
  }

  return {
    isActive: data.is_active === true,
    industryType: data.industry_type ?? 'pool',
    customIndustryLabel: data.custom_industry_label ?? null,
    tenantName: typeof data.name === 'string' ? data.name : null,
  };
}
